import { Dialog } from '@ark-ui/react'
import { Accordion, AccordionRoot } from '@ark-ui/react/accordion'
import {
  createLink,
  type LinkProps,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import clsx from 'clsx'
import type { UiIconName } from 'grafbase-components/ui/icon/icon'
import { Icon } from 'grafbase-components/ui/icon/icon'
import { FullpageSpinner } from 'grafbase-components/ui/loaders/spinner'
import {
  type ComponentProps,
  createContext,
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react'
import Skeleton from 'react-loading-skeleton'
import { useFragment } from 'react-relay'
import { graphql } from 'relay-runtime'
import { Config } from '#app/config'
import { HelpModal } from '#app/pages/dashboard/help-modal'
import { ProfileMenu } from '#app/pages/dashboard/profile-menu'
import type { sideNavBottomLinksFragment$key } from '#gen/relay/sideNavBottomLinksFragment.graphql'
import type { sideNavFragment$key } from '#gen/relay/sideNavFragment.graphql'
import { useHasMounted } from '#lib/hooks/use-has-mounted'
import { websitePaths } from '#lib/routing/website-paths'
import { MainContainer } from '../../main-container/main-container'
import { SideNavStyles as styles } from './side-nav.css'
import { useCameFromSection } from './utils'

const fallback = (
  <MainContainer layout="full">
    <FullpageSpinner />
  </MainContainer>
)

const fragment = graphql`
  fragment sideNavFragment on User {
    ...sideNavBottomLinksFragment
  }
`

function Root(
  props: PropsWithChildren<{
    content: ReactNode
  }>,
) {
  const [isOverriddenByChild, setIsOverriddenByChild] = useState(false)
  const globalCtx = useContext(SideNavGlobalContext)

  const paramsKey = useParams({
    strict: false,
    select: ({ account = '__', graph = '__', branch = '__' }) => {
      return `${account}/${graph}/${branch}`
    },
  })

  if (!globalCtx)
    throw new Error(
      'SideNavGlobalContext is not provided. Please wrap your component with SideNavGlobalContext.Provider.',
    )

  const bottomLinksDataRef = useFragment(fragment, globalCtx.sideNavDataRef)

  const parentCtx = useContext(SideNavContext)

  const override = useCallback((state: boolean) => {
    setIsOverriddenByChild(state)
  }, [])

  const handleNavInteraction = useCallback(
    (
      event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
      const target = event.target as HTMLElement

      // Check if the event is a navigation link interaction
      const isNavLink = target.closest('a') !== null

      if (isNavLink) {
        // For keyboard events, only respond to Enter and Space
        if (event.type === 'keydown') {
          const keyboardEvent = event as React.KeyboardEvent<HTMLElement>
          if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
            globalCtx.setIsOpen(false)
          }
        } else if (event.type === 'click') {
          globalCtx.setIsOpen(false)
        }
      }
    },
    [globalCtx],
  )

  useEffect(() => {
    parentCtx.override(true)

    return () => {
      parentCtx.override(false)
    }
  }, [parentCtx.override])

  const hasMounted = useHasMounted()

  return (
    <SideNavContext.Provider value={{ override }}>
      {/* Only render nested routes matches */}
      {isOverriddenByChild && props.content}

      {/* Render the sidebar and nested routes matches */}
      {!isOverriddenByChild && (
        <>
          <nav
            key={paramsKey}
            className={styles.nav}
            aria-label="sidebar"
            data-testid="sidenav"
            data-open={globalCtx.isOpen ? 'true' : 'false'}
            onClick={handleNavInteraction}
            onKeyDown={handleNavInteraction}
          >
            {props.children}
            <BottomLinks dataRef={bottomLinksDataRef} />
          </nav>
          {/* On the inital render, use the parent (global) suspense boundary. After mounting, it's okay to show more granular loading spinner. */}
          {hasMounted ? (
            <Suspense fallback={fallback}>{props.content}</Suspense>
          ) : (
            props.content
          )}
        </>
      )}
    </SideNavContext.Provider>
  )
}

const Group = ({ children }: PropsWithChildren) => (
  <ul className={styles.ul}>{children}</ul>
)

const GroupLabel = ({ children }: PropsWithChildren) => (
  <p className={styles.groupLabel}>{children}</p>
)

const NestedGroup = ({
  children,
  open,
}: PropsWithChildren<{ open?: boolean }>) => {
  const id = useId()

  return (
    <AccordionRoot collapsible value={open ? [id] : []}>
      <Accordion.Item value={id}>{children}</Accordion.Item>
    </AccordionRoot>
  )
}

const NestedGroupTrigger = createLink(function Item({
  icon,
  disableActiveStyles = false,
  ...props
}: ComponentProps<'a'> & {
  icon?: UiIconName
  disableActiveStyles?: boolean
}) {
  return (
    <div className={styles.nestedGroupTriggerContainer}>
      <a
        {...props}
        className={styles.link({ disableActiveStyles })}
        title={typeof props.children === 'string' ? props.children : undefined}
      >
        {icon && <Icon icon={icon} size="md" />}
        <span className={styles.linkText}>{props.children}</span>
      </a>
    </div>
  )
})

const NestedGroupContent = ({
  children,
  withBorder = true,
  marginInlineStart,
}: PropsWithChildren<{ withBorder?: boolean; marginInlineStart?: number }>) => (
  <Accordion.ItemContent
    className={styles.nestedGroupContent({ withBorder })}
    style={marginInlineStart ? { marginInlineStart } : undefined}
    data-nested-menu
  >
    {children}
  </Accordion.ItemContent>
)

const Item = createLink(function Item({
  icon,
  ...props
}: ComponentProps<'a'> & {
  icon?: UiIconName
}) {
  return (
    <li>
      <a
        {...props}
        className={styles.link()}
        title={typeof props.children === 'string' ? props.children : undefined}
      >
        {icon && <Icon icon={icon} className={styles.itemIcon} size="md" />}
        <span className={styles.linkText}>{props.children}</span>
      </a>
    </li>
  )
})

const ItemSkeleton = () => (
  <li>
    <div className={clsx(styles.link())}>
      <span className={styles.linkText}>
        <Skeleton width="10ch" />
      </span>
    </div>
  </li>
)

const EnterpriseOnlyItem = ({
  icon,
  ...props
}: {
  children: ReactNode
  icon?: UiIconName
}) => (
  <li>
    <a
      href={websitePaths.contactSales}
      target="_blank"
      className={styles.link()}
      title={typeof props.children === 'string' ? props.children : undefined}
      {...props}
    >
      {icon && <Icon icon={icon} size="md" />}
      <span className={styles.linkText}>{props.children}</span>
      <span className={styles.contactSalesBadge}>Contact Sales</span>
    </a>
  </li>
)

const BackLink = ({
  children,
  fallbackLink,
  ...props
}: React.ComponentProps<'button'> & { fallbackLink: LinkProps }) => {
  const { location, update } = useCameFromSection()
  const navigate = useNavigate()

  return (
    <button
      {...props}
      className={clsx(styles.backButton, props.className)}
      title={typeof children === 'string' ? children : undefined}
      onClick={e => {
        if (location)
          navigate({
            to: location.pathname,
            state: location.state,
            hash: location.hash,
            search: location.search,
          })
        else navigate(fallbackLink)

        update(null)

        props.onClick?.(e)
      }}
    >
      <Icon icon="arrow-right" style={{ rotate: '180deg' }} size="md" />
      <span className={styles.linkText}>{children}</span>
    </button>
  )
}

const Separator = () => <hr className={styles.separator} />

const bottomLinksFragment = graphql`
  fragment sideNavBottomLinksFragment on User {
    ...helpModalFragment
    ...profileMenuFragment
  }
`

const BottomLinks = (props: { dataRef: sideNavBottomLinksFragment$key }) => {
  const data = useFragment(bottomLinksFragment, props.dataRef)

  const params = useParams({
    strict: false,
    select({ account }) {
      return { account }
    },
  })

  return (
    <div className={styles.bottomLinks}>
      <a
        href={websitePaths.docs.overview}
        target="_blank"
        className={styles.link()}
      >
        <Icon icon={'book'} size="md" />
        <span className={styles.linkText}>Docs</span>
      </a>

      <a
        href={websitePaths.changelog}
        target="_blank"
        className={styles.link()}
      >
        <Icon icon={'split-diff'} size="md" />
        <span className={styles.linkText}>Changelog</span>
      </a>

      {Config.IS_MANAGED_BUILD && (
        <HelpModal
          dataRef={data}
          activeAccountSlug={params.account}
          key={params.account}
        >
          <Dialog.Trigger className={styles.link()}>
            <Icon icon={'information'} size="md" />
            <span className={styles.linkText}>Help</span>
          </Dialog.Trigger>
        </HelpModal>
      )}

      <ProfileMenu dataRef={data} triggerClassName={styles.link()} />
    </div>
  )
}

export const SideNav = {
  Root,
  Group,
  GroupLabel,
  Item,
  ItemSkeleton,
  EnterpriseOnlyItem,
  Separator,
  BackLink,
  NestedGroup,
  NestedGroupTrigger,
  NestedGroupContent,
}

type SideNavState = {
  override: (state: boolean) => void
}

const SideNavContext = createContext<SideNavState>({
  override: () => {},
})

type SideNavGlobalState = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  sideNavDataRef: sideNavFragment$key
}

export const SideNavGlobalContext = createContext<SideNavGlobalState | null>(
  null,
)
